import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { query, where, getDocs, collection, getDoc, doc, collectionGroup } from 'firebase/firestore';
import { db } from '../firebase';
import { logger } from '../shared/logger';
import { memberApi, completeCheckout } from '../api';
import { auth } from '../firebase';
import { sendEmailVerification} from 'firebase/auth';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { ErrorDisplay } from '../components/ErrorDisplay';
import { ErrorHandler } from '../errors';

interface RegisterFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  inviteCode: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  inviteCode?: string;
  form?: string;
}

interface InviteCode {
  id: string;
  code: string;
  facilityId: string;
  businessId: string;
  createdAt: string;
  expiresAt: string;
  isUsed: boolean;
}

export const NewMemberRegister: React.FC = () => {
  const [step, setStep] = useState<'invite' | 'register' | 'verify'>('invite');
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    inviteCode: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [inviteCodeId, setInviteCodeId] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const { login, currentUser, refreshUserData, sendVerificationEmail, isEmailVerified } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);
  const [completingCheckout, setCompletingCheckout] = useState(false);
  const { error, handleError, clearError } = useErrorHandler();

  // Add useEffect to check for invite code in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteCode = params.get('inviteCode');
    const ppu = params.get('ppu');
    const facilityIdParam = params.get('facilityId');
    const businessIdParam = params.get('businessId');
    if (inviteCode) {
      setFormData(prev => ({
        ...prev,
        inviteCode: inviteCode.toUpperCase()
      }));
    }
    if (ppu === '1' && facilityIdParam) {
      setFacilityId(facilityIdParam);
      setBusinessId(businessIdParam);
      setStep('register');
    }
  }, [location.search]);

  const validateInviteCode = async (code: string): Promise<InviteCode> => {
    try {
      // First, we need to find which business/facility this code belongs to
      // We'll use a collectionGroup query just once to find the path
      const q = query(
        collectionGroup(db, 'inviteCodes'),
        where('code', '==', code.toUpperCase())
      );
      
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        throw new Error('Invalid invite code');
      }

      const inviteCodeDoc = querySnapshot.docs[0];
      const pathParts = inviteCodeDoc.ref.path.split('/');
      const businessId = pathParts[1]; // businesses/{businessId}/facilities/...
      const facilityId = pathParts[3]; // businesses/{businessId}/facilities/{facilityId}/...

      // Now that we have the businessId and facilityId, we can get the invite code directly
      const inviteCodeRef = doc(db, 'businesses', businessId, 'facilities', facilityId, 'inviteCodes', inviteCodeDoc.id);
      const inviteCodeSnap = await getDoc(inviteCodeRef);
      
      if (!inviteCodeSnap.exists()) {
        throw new Error('Invite code not found');
      }

      const inviteCode = {
        id: inviteCodeSnap.id,
        ...inviteCodeSnap.data()
      } as InviteCode;
      
      if (inviteCode.isUsed) {
        throw new Error('This invite code has already been used');
      }
      
      if (new Date(inviteCode.expiresAt) < new Date()) {
        throw new Error('This invite code has expired');
      }

      // Check if facility has Stripe account set up
      const facilityDoc = await getDoc(doc(db, 'businesses', businessId, 'facilities', facilityId));
      if (!facilityDoc.exists) {
        throw new Error('Facility not found');
      }

      const facilityData = facilityDoc.data();
      if (!facilityData || !facilityData.stripeAccountId) {
        throw new Error('Facility is not set up for payments yet');
      }

      // Check if facility has any courts
      const courtsSnapshot = await getDocs(collection(db, 'businesses', businessId, 'facilities', facilityId, 'courts'));
      if (courtsSnapshot.empty) {
        throw new Error('Facility has no courts set up yet');
      }
      
      return {
        ...inviteCode,
        businessId
      };
    } catch (error) {
      // Handle Firestore permission errors
      if (error instanceof Error && error.message.includes('Missing or insufficient permissions')) {
        throw new Error('Unable to validate invite code. Please try again or contact support.');
      }
      // Re-throw other errors
      throw error;
    }
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    clearError();

    try {
      const inviteCode = await validateInviteCode(formData.inviteCode);
      setFacilityId(inviteCode.facilityId);
      setBusinessId(inviteCode.businessId);
      setInviteCodeId(inviteCode.id);
      sessionStorage.setItem('invite_code_id', inviteCode.id);
      sessionStorage.setItem('business_id', inviteCode.businessId);
      setStep('register');
    } catch (err) {
      setErrors({ inviteCode: err instanceof Error ? err.message : 'Invalid invite code' });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s-]{10,}$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/;

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordRegex.test(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one number';
    }

    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';

    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(location.search);
    const isPPU = params.get('ppu') === '1';

    if (!validateForm() || !facilityId || (!isPPU && !inviteCodeId) || !businessId) {
      setErrors({
        form: 'Please fill in all fields'
      });
    }

    try {
      setLoading(true);
      clearError();
      setErrors({});

      if (!facilityId || !businessId) {
        logger.error('Missing facility ID or business ID');
        return;
      }

      const basePayload = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        facilityId: facilityId,
        businessId: businessId
      };

      // Use appropriate registration endpoint based on facility type
      const response = isPPU ? 
        await memberApi.registerPPU(basePayload) : 
        await memberApi.register({ ...basePayload, inviteCodeId: inviteCodeId! });

      sessionStorage.setItem('invite_code_id', inviteCodeId || '');
      sessionStorage.setItem('pending_registration_email', formData.email);

      // Wait for auth state to settle
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Login with the newly created account
      await login(formData.email, formData.password);

      // Wait for auth state to settle again
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Get the current user from auth
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Failed to get current user after login');
      }

      // Send verification email
      await sendEmailVerification(user);
      setVerificationSent(true);
      setStep('verify');
    } catch (err) {
      const apiError = ErrorHandler.handle(err);
      
      // Check if it's an email already exists error
      if (apiError.error.code === 'EMAIL_EXISTS' || 
          apiError.error.message.toLowerCase().includes('email already exists')) {
        setErrors(prev => ({
          ...prev,
          email: 'This email is already registered. Please use a different email or try logging in.'
        }));
      } else {
        // Handle other errors
        handleError(err);
        setErrors(prev => ({
          ...prev,
          form: apiError.error.message
        }));
      }
      
      logger.error('Registration error:', { 
        error: apiError.error,
        email: formData.email 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReloadVerification = async () => {
    try {
      setLoading(true);
      logger.info('Checking verification status', { 
        email: formData.email,
        currentVerified: isEmailVerified 
      });

      // Get the current user directly from auth
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user found');
      }

      // Force reload and check verification status
      await user.reload();
      const isVerified = user.emailVerified;
      logger.info('Verification status after reload', { 
        email: formData.email,
        isVerified 
      });

      const isPPU = new URLSearchParams(location.search).get('ppu') === '1';
      if (isVerified) {
        setVerificationMessage(`Email is now verified! Click "Continue to ${isPPU ? 'Dashboard' : 'Plan Selection'}" to proceed.`);
      } else {
        setVerificationMessage('Email not verified yet. Please check your email and click the verification link.');
      }

      // Update the context state
      await refreshUserData();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Error checking verification status:', { 
        error: error.message,
        email: formData.email,
        currentVerified: isEmailVerified 
      });
      setVerificationMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationComplete = async () => {
    try {
      setLoading(true);
      logger.info('Attempting to proceed to plan selection', { 
        email: formData.email,
        currentVerified: isEmailVerified 
      });

      // Get the current user directly from auth
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user found');
      }

      // Force reload and check verification status
      await user.reload();
      const isVerified = user.emailVerified;
      logger.info('Verification status before proceeding', { 
        email: formData.email,
        isVerified 
      });

      if (!isVerified) {
        setVerificationMessage('Email not verified yet. Please check your email and click the verification link.');
        return;
      }

      // Update the context state
      await refreshUserData();

      const isPPU = new URLSearchParams(location.search).get('ppu') === '1';
      
      if (isPPU) {
        // For PPU registration, go directly to dashboard
        navigate('/member/dashboard');
      } else {
        // For regular registration, go to plan selection
        const successUrl = encodeURIComponent(`${window.location.origin}/member/register?success=true&session_id={CHECKOUT_SESSION_ID}`);
        const cancelUrl = encodeURIComponent(`${window.location.origin}/member/plans?facilityId=${facilityId}`);
        navigate(`/member/plans?facilityId=${facilityId}&businessId=${businessId}&inviteCodeId=${inviteCodeId}&successUrl=${successUrl}&cancelUrl=${cancelUrl}`);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Error proceeding after verification:', { 
        error: error.message,
        email: formData.email,
        currentVerified: isEmailVerified 
      });
      setVerificationMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await sendVerificationEmail();
      setVerificationSent(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Error resending verification email:', { error: error.message });
      setErrors(prev => ({
        ...prev,
        form: error.message
      }));
    } finally {
      setLoading(false);
    }
  };

  // Add new function to handle checkout completion
  const handleCheckoutCompletion = async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const success = params.get('success');

    if (sessionId && success === 'true') {
      setCompletingCheckout(true);
      try {
        // Try multiple sources for facilityId and businessId
        const storedFacilityId = sessionStorage.getItem('facilityId') || 
                                localStorage.getItem('facilityId') ||
                                new URLSearchParams(window.location.search).get('facilityId');
        
        const storedBusinessId = sessionStorage.getItem('businessId') ||
                                localStorage.getItem('businessId') ||
                                new URLSearchParams(window.location.search).get('businessId');
        
        console.log('Checkout completion data:', {
          storedFacilityId,
          storedBusinessId,
          sessionId,
          currentUser: currentUser?.uid
        });
        
        if (!storedFacilityId || !storedBusinessId) {
          console.error('Missing facility ID or business ID for checkout completion');
          navigate('/member/login?registration=incomplete');
          return;
        }

        // Try to complete the checkout to update subscription data
        console.log('Completing checkout with:', { sessionId, facilityId: storedFacilityId, businessId: storedBusinessId });
        await completeCheckout(sessionId, storedBusinessId, storedFacilityId);
        
        // Clear any stored data and redirect to login with success message
        sessionStorage.removeItem('facilityId');
        sessionStorage.removeItem('businessId');
        sessionStorage.removeItem('pending_registration_email');
        localStorage.removeItem('facilityId');
        localStorage.removeItem('businessId');
        
        console.log('Checkout completed successfully, redirecting to login');
        navigate('/member/login?registration=complete');
      } catch (error: any) {
        console.error('Error completing checkout:', error);
        logger.error('Error completing checkout:', { 
          error: error.error?.message || error.message || 'Unknown error',
          sessionId,
          currentUser: currentUser?.uid
        });
        // Redirect to login with error message
        navigate('/member/login?registration=failed');
      } finally {
        setCompletingCheckout(false);
      }
    }
  };

  useEffect(() => {
    handleCheckoutCompletion();
  }, [location.search, navigate]);

  if (completingCheckout) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const renderInviteStep = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {facilityId ? 'Register for Facility' : 'Enter Your Invite Code'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {facilityId ? 'You are registering for a Pay-Per-Use facility. No invite code is required.' : 'Please enter the invite code provided to you to begin registration'}
          </p>
        </div>
        {!facilityId && (
          <form className="mt-8 space-y-6" onSubmit={handleInviteSubmit}>
            <div>
              <label htmlFor="inviteCode" className="sr-only">Invite Code</label>
              <input
                id="inviteCode"
                name="inviteCode"
                type="text"
                required
                className={`appearance-none rounded relative block w-full px-3 py-2 border ${
                  errors.inviteCode ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                placeholder="Invite Code"
                value={formData.inviteCode}
                onChange={handleInputChange}
              />
              {errors.inviteCode && (
                <p className="mt-1 text-sm text-red-600">{errors.inviteCode}</p>
              )}
            </div>
            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Continue'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  const renderRegisterStep = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create Your Account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please fill in your details to create your account
          </p>
        </div>
        {error && (
          <ErrorDisplay 
            error={error} 
            onDismiss={clearError}
          />
        )}
        {errors.form && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-700">{errors.form}</p>
            {errors.form.includes('log in instead') && (
              <button
                type="button"
                onClick={() => navigate('/member/login')}
                className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                Go to Login
              </button>
            )}
          </div>
        )}
        <form className="mt-8 space-y-6" onSubmit={handleRegisterSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="sr-only">First Name</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  className={`appearance-none rounded relative block w-full px-3 py-2 border ${
                    errors.firstName ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>
              <div>
                <label htmlFor="lastName" className="sr-only">Last Name</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  className={`appearance-none rounded relative block w-full px-3 py-2 border ${
                    errors.lastName ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="email" className="sr-only">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className={`appearance-none rounded relative block w-full px-3 py-2 border ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
              />
              {errors.email && (
                <ErrorDisplay error={errors.email} />
              )}
            </div>

            <div className="mt-4">
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className={`appearance-none rounded relative block w-full px-3 py-2 border ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div className="mt-4">
              <label htmlFor="phone" className="sr-only">Phone</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                required
                className={`appearance-none rounded relative block w-full px-3 py-2 border ${
                  errors.phone ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                placeholder="Phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Add new render function for verification step
  const renderVerificationStep = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify Your Email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent a verification email to {formData.email}. Please check your inbox and click the verification link.
          </p>
        </div>
        <div className="mt-8 space-y-6">
          {verificationMessage && (
            <div className={`rounded-md p-4 ${isEmailVerified ? 'bg-green-50' : 'bg-red-50'}`}>
              <p className={`text-sm ${isEmailVerified ? 'text-green-700' : 'text-red-700'}`}>
                {verificationMessage}
              </p>
            </div>
          )}
          
          <div className="flex flex-col space-y-4">
            <button
              type="button"
              onClick={handleVerificationComplete}
              disabled={loading || !isEmailVerified}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Checking...' : (new URLSearchParams(location.search).get('ppu') === '1' ? 'Continue to Login' : 'Continue to Plan Selection')}
            </button>

            <button
              type="button"
              onClick={handleReloadVerification}
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Checking...' : 'Check Verification Status'}
            </button>

            <button
              type="button"
              onClick={handleResendVerification}
              disabled={loading || verificationSent}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {verificationSent ? 'Verification Email Sent' : 'Resend Verification Email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Floating button for PPU facilities
  const PPUFacilitiesButton = () => (
    <Link
      to="/member/ppu-facilities"
      className="fixed bottom-6 right-6 z-50 bg-indigo-600 text-white px-4 py-2 rounded-full shadow-lg hover:bg-indigo-700 transition-all text-sm font-medium"
      style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
    >
      Don't have an invite code? View PPU facilities here
    </Link>
  );

  // Update the return statement to include the verification step
  return (
    <>
      {step === 'invite' && (
        <>
          {renderInviteStep()}
          <PPUFacilitiesButton />
        </>
      )}
      {step === 'register' && renderRegisterStep()}
      {step === 'verify' && renderVerificationStep()}
    </>
  );
};

export default NewMemberRegister; 